import React from 'react';
import { keyframes } from 'styled-components';
import { MdErrorOutline, MdInbox } from 'react-icons/md';

import { anelDeFoco, comAlfa, styledDs as styled } from '../../../styles/theme';
import { DsButton } from '../Button';

const pulse = keyframes`
  0% { opacity: 0.55; }
  50% { opacity: 1; }
  100% { opacity: 0.55; }
`;

export const DsSkeleton = styled.div<{ $width?: string; $height?: string }>`
  width: ${({ $width }) => $width || '100%'};
  height: ${({ $height }) => $height || '16px'};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.grid};
  animation: ${pulse} 1.4s ease-in-out infinite;
`;

const StateWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 40px 16px;
  text-align: center;
  font-family: ${({ theme }) => theme.font.family};

  svg {
    width: 28px;
    height: 28px;
    color: ${({ theme }) => theme.colors.textMuted};
  }

  h3 {
    margin: 0;
    font-size: ${({ theme }) => theme.font.sizeLg};
    line-height: ${({ theme }) => theme.leading.h4};
    font-weight: ${({ theme }) => theme.weight.semibold};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  p {
    margin: 0;
    max-width: 380px;
    font-size: ${({ theme }) => theme.font.sizeMd};
    line-height: ${({ theme }) => theme.leading.corpo};
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  button {
    margin-top: 6px;
  }
`;

export type DsEmptyStateProps = {
  title: string;
  description?: string;
  action?: React.ReactNode;
};

export const DsEmptyState: React.FC<DsEmptyStateProps> = ({ title, description, action }) => (
  <StateWrap>
    <MdInbox aria-hidden="true" />
    <h3>{title}</h3>
    {description && <p>{description}</p>}
    {action}
  </StateWrap>
);

export type DsErrorStateProps = {
  /** O que falhou, na voz da tela: "Não foi possível carregar as publicações". */
  title?: string;
  message?: string;
  onRetry?: () => void;
  /** O botão nomeia a ação que ele faz; "Tentar novamente" só cabe no caso comum. */
  retryLabel?: string;
};

export const DsErrorState: React.FC<DsErrorStateProps> = ({
  title = 'Não foi possível carregar',
  message = 'O servidor não respondeu. Tente de novo em alguns instantes.',
  onRetry,
  retryLabel = 'Tentar novamente',
}) => (
  <StateWrap role="alert">
    <MdErrorOutline aria-hidden="true" />
    <h3>{title}</h3>
    <p>{message}</p>
    {onRetry && (
      <DsButton variant="secondary" onClick={onRetry}>
        {retryLabel}
      </DsButton>
    )}
  </StateWrap>
);

/**
 * A tela cheia de erro. Ela pinta o próprio fundo porque quem a mostra é o
 * ErrorBoundary do topo: nesse ponto a casca do app não montou, e sem fundo
 * próprio o tema escuro escrevia texto claro sobre o branco do body — era o
 * título invisível que sobrava um buraco entre o ícone e a mensagem.
 */
const PaginaDeErro = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow: auto;
  background: ${({ theme }) => theme.colors.page};
  font-family: ${({ theme }) => theme.font.family};
`;

const CartaoDeErro = styled.div`
  display: flex;
  width: 100%;
  max-width: 420px;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 32px 28px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.xl};
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.shadow.lg};
  text-align: center;

  h1 {
    margin: 0;
    font-size: ${({ theme }) => theme.font.sizeLg};
    line-height: ${({ theme }) => theme.leading.h4};
    font-weight: ${({ theme }) => theme.weight.semibold};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  p {
    margin: 0;
    max-width: 34ch;
    font-size: ${({ theme }) => theme.font.sizeSm};
    line-height: ${({ theme }) => theme.leading.corpo};
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

/**
 * Círculo neutro com o glifo vermelho: o guia reserva o vermelho para sinal de
 * sistema e proíbe usá-lo como fundo de área, então quem sinaliza é o ícone.
 */
const SeloDeErro = styled.div`
  display: grid;
  place-items: center;
  width: 52px;
  height: 52px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => comAlfa(theme.colors.textMuted, 0.12)};

  svg {
    width: 26px;
    height: 26px;
    color: ${({ theme }) => theme.colors.statusCritical};
  }
`;

const AcoesDoErro = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
`;

const DetalheTecnico = styled.details`
  width: 100%;
  margin-top: 2px;
  text-align: left;

  summary {
    cursor: pointer;
    font-size: ${({ theme }) => theme.font.sizeXs};
    color: ${({ theme }) => theme.colors.textMuted};
    list-style: none;
  }

  summary::-webkit-details-marker {
    display: none;
  }

  summary:focus-visible {
    ${anelDeFoco()}
  }

  pre {
    margin: 8px 0 0;
    max-height: 140px;
    overflow: auto;
    padding: 8px 10px;
    border-radius: ${({ theme }) => theme.radius.md};
    background: ${({ theme }) => theme.colors.page};
    color: ${({ theme }) => theme.colors.textSecondary};
    font-family: ${({ theme }) => theme.font.mono};
    font-size: ${({ theme }) => theme.font.sizeXs};
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

export type DsFullPageErrorProps = {
  title?: string;
  message?: string;
  onRetry?: () => void;
  retryLabel?: string;
  /** A saída de emergência quando recarregar não resolve. */
  onHome?: () => void;
  homeLabel?: string;
  /** A mensagem do erro, para quem for pedir suporte. Fica recolhida. */
  detail?: string;
};

export const DsFullPageError: React.FC<DsFullPageErrorProps> = ({
  title = 'Não foi possível abrir a tela',
  message = 'O servidor não respondeu. Tente de novo em alguns instantes.',
  onRetry,
  retryLabel = 'Tentar novamente',
  onHome,
  homeLabel = 'Ir para o início',
  detail,
}) => (
  <PaginaDeErro>
    <CartaoDeErro role="alert">
      <SeloDeErro>
        <MdErrorOutline aria-hidden="true" />
      </SeloDeErro>
      <h1>{title}</h1>
      <p>{message}</p>
      {(onRetry || onHome) && (
        <AcoesDoErro>
          {onRetry && (
            <DsButton variant="primary" block onClick={onRetry}>
              {retryLabel}
            </DsButton>
          )}
          {onHome && (
            <DsButton variant="secondary" block onClick={onHome}>
              {homeLabel}
            </DsButton>
          )}
        </AcoesDoErro>
      )}
      {detail && (
        <DetalheTecnico>
          <summary>Detalhes técnicos</summary>
          <pre>{detail}</pre>
        </DetalheTecnico>
      )}
    </CartaoDeErro>
  </PaginaDeErro>
);
